import { erc20Abi, Abi } from "viem";
import { createContext, useContext, useEffect, useState } from "react";
import usePublicClient from "@/hooks/usePublicClient";
import { useTokenList } from "@/hooks/useTokens";
import { FeeAmount } from "@uniswap/v3-sdk";
import BigNumber from "bignumber.js";
import { useAccount } from "wagmi";
import { useNavigate, useLocation } from "react-router-dom";
import LogoHome from "@/components/LogoHome";
import { DEFAULT_CHAIN_ID } from "@/constants";
import { ethAddress, usdcInfo, wethInfo } from "@/constants/tokens";
import { MULTICALL_ADDRESSES, QUOTER_ADDRESSES } from "@uniswap/sdk-core";
import Quoter from "@/constants/abis/Quoter.json";
import { formatAmount, parseAmount } from "@/utils/formatBalance";
import axios from "axios";

export interface TokenInfo {
  chainId: number;
  address: `0x${string}`;
  decimals: number;
  symbol: string;
  name: string;
  balance: string;
  value: string;
  icon: string;
  recommended?: boolean;
}

const UserInfoContext = createContext<{
  totalUsdValue: string;
  tokenInfo: {
    [key: string]: TokenInfo;
  };
  balanceLoading: boolean;
  ethPrice: string;
  currencyInfo: {
    localCurrency: string;
    exchangeRate: number;
  };
  showUsd: boolean;
  showBalance: boolean;
  handleSwitchCurrency: () => void;
  handleSetCurrency: (currency: string, setToLocal?: boolean) => Promise<void>;
  handleShowBalance: (show: boolean) => void;
}>({
  totalUsdValue: "0",
  tokenInfo: {},
  balanceLoading: false,
  ethPrice: "0",
  currencyInfo: {
    localCurrency: "",
    exchangeRate: 0,
  },
  showUsd: false,
  showBalance: false,
  handleSwitchCurrency: () => {},
  handleSetCurrency: (currency: string, setToLocal?: boolean) =>
    Promise.resolve(),
  handleShowBalance: (show: boolean) => {},
});

export const UserInfoProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isConnected, address } = useAccount();
  const [balanceLoading, setBalanceLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const publicClient = usePublicClient();
  const [totalUsdValue, setTotalUsdValue] = useState("0");
  const [ethPrice, setEthPrice] = useState("0");
  const [showBalance, setShowBalance] = useState(
    localStorage.getItem("showBalance") === "true"
  );
  const [tokenInfo, setTokenInfo] = useState<{
    [key: string]: TokenInfo;
  }>({});
  const [currencyInfo, setCurrencyInfo] = useState({
    localCurrency: "",
    exchangeRate: 0,
  });
  const [showUsd, setShowUsd] = useState(
    localStorage.getItem("showUsd") === "true"
  );

  const handleShowBalance = (show: boolean) => {
    setShowBalance(show);
    localStorage.setItem("showBalance", show.toString());
  };

  // 切换货币
  const handleSwitchCurrency = () => {
    setShowUsd(!showUsd);
    localStorage.setItem("showUsd", showUsd.toString());
  };

  // 获取IP对应的货币代码 获取汇率
  const fetchCurrencyInfo = async () => {
    try {
      const storageCurrency = localStorage.getItem("localCurrency");
      let localCurrency = "";
      if (storageCurrency) {
        localCurrency = storageCurrency;
      } else {
        const { data: ipData } = await axios.get("https://ipapi.co/json/");
        localCurrency = ipData.currency;
      }
      handleSetCurrency(localCurrency, false);
    } catch (error) {
      console.error("获取货币信息失败:", error);
      handleSetCurrency("USD", false);
    }
  };

  const handleSetCurrency = async (currency: string, setToLocal?: boolean) => {
    const { data: rateData } = await axios.get(
      `https://open.er-api.com/v6/latest/USD`
    );
    let _currency = currency;
    if (!rateData.rates[currency]) {
      _currency = "USD";
    }
    const exchangeRate = rateData.rates[_currency];
    if (setToLocal) {
      localStorage.setItem("localCurrency", _currency);
    }
    setCurrencyInfo({ localCurrency: _currency, exchangeRate });
  };

  const tokens = useTokenList();

  const handleBalance = async () => {
    // 构建所有需要的合约调用，包括 ETH 余额和代币余额查询
    const contractCalls = tokens.flatMap((token) => {
      if (token.address === ethAddress) {
        return {
          address: MULTICALL_ADDRESSES[DEFAULT_CHAIN_ID]!,
          abi: [
            {
              inputs: [
                { internalType: "address", name: "addr", type: "address" },
              ],
              name: "getEthBalance",
              outputs: [
                { internalType: "uint256", name: "balance", type: "uint256" },
              ],
              stateMutability: "view",
              type: "function",
            },
          ],
          functionName: "getEthBalance",
          args: [address],
        };
      }

      return {
        address: token.address as `0x${string}`,
        abi: erc20Abi as Abi,
        functionName: "balanceOf",
        args: [address],
      };
    });

    // 获取余额结果
    const tokenBalanceResults = await publicClient.multicall({
      contracts: contractCalls as any,
    });

    // 过滤掉余额为0
    const filteredTokens = tokens.map((token, index) => ({
      ...token,
      balance:
        tokenBalanceResults[index].status === "success"
          ? (tokenBalanceResults[index].result as BigInt).toString()
          : "0",
    }));

    // 过滤掉 USDC
    const nonUsdcTokens = filteredTokens.filter(
      (token) => token.address.toLowerCase() !== usdcInfo.address.toLowerCase()
    );

    // 过滤掉 USDC，只查询其他代币的价格
    const valueQuoteCalls = nonUsdcTokens
      .filter((token) => {
        return token.balance != "0";
      })
      .map((token, index) => {
        const tokenIn =
          token.address === ethAddress ? wethInfo.address : token.address;

        return {
          address: QUOTER_ADDRESSES[DEFAULT_CHAIN_ID]! as `0x${string}`,
          abi: Quoter,
          functionName: "quoteExactInputSingle",
          args: [
            {
              tokenIn: tokenIn,
              tokenOut: usdcInfo.address,
              fee: FeeAmount.MEDIUM,
              amountIn: token.balance,
              sqrtPriceLimitX96: 0,
            },
          ],
        };
      });

    valueQuoteCalls.unshift({
      address: QUOTER_ADDRESSES[DEFAULT_CHAIN_ID]! as `0x${string}`,
      abi: Quoter,
      functionName: "quoteExactInputSingle",
      args: [
        {
          tokenIn: wethInfo.address,
          tokenOut: usdcInfo.address,
          fee: FeeAmount.MEDIUM,
          amountIn: parseAmount("1", wethInfo.decimals),
          sqrtPriceLimitX96: 0,
        },
      ],
    });

    try {
      let count = 0;
      // 批量获取所有价格
      const [ethPriceResult, ...valueResults] = (await publicClient.multicall({
        contracts: valueQuoteCalls as any,
      })) as { result: [BigInt] }[];

      setEthPrice(
        formatAmount(
          (ethPriceResult.result[0] as BigInt).toString(),
          usdcInfo.decimals
        )
      );

      console.log(valueResults, "valueResults");

      let totalUsdValue = new BigNumber(0);
      const tokenInfo: { [key: string]: TokenInfo } = {};

      // 处理其他代币
      filteredTokens.forEach((token, index) => {
        if (token.address === usdcInfo.address) {
          const value = token.balance;
          tokenInfo[token.address] = {
            ...token,
            value,
          };
          totalUsdValue = totalUsdValue.plus(value);
          return;
        }

        if (token.balance === "0") {
          tokenInfo[token.address] = {
            ...token,
            value: "0",
          };
          return;
        }

        const amountOut = (
          valueResults[count++].result[0] as BigInt
        ).toString();
        const value = amountOut;
        totalUsdValue = totalUsdValue.plus(value);
        tokenInfo[token.address] = {
          ...token,
          value,
        };
      });

      setTotalUsdValue(totalUsdValue.toFixed(0));
      setTokenInfo(tokenInfo);
      setBalanceLoading(false);
    } catch (error) {
      console.error("Error in handleBalance:", error);
      // 错误处理：设置默认值
      setTotalUsdValue("0");
      setTokenInfo({});
    } finally {
      setBalanceLoading(false);
    }
  };

  useEffect(() => {
    if (!address) {
      setTokenInfo({});
      setTotalUsdValue("0");
      return;
    }
    handleBalance();
  }, [address]);

  // 处理初始加载和最小显示时间
  useEffect(() => {
    if (initialCheckDone) return;
    const startTime = Date.now();
    const minLoadingTime = 2000; // 最少显示 2 秒

    const timer = setTimeout(() => {
      if (!isConnected) {
        if (location.pathname !== "/login") {
          const returnUrl = encodeURIComponent(
            location.pathname + location.search
          );
          navigate(`/login?returnUrl=${returnUrl}`);
        }
      }
      setInitialCheckDone(true);
    }, minLoadingTime);

    // 如果检测到钱包已连接，也要确保至少显示 2 秒
    if (isConnected) {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

      clearTimeout(timer);
      setTimeout(() => {
        setInitialCheckDone(true);
      }, remainingTime);
    }

    return () => clearTimeout(timer);
  }, [isConnected, initialCheckDone]);

  // 处理钱包断开连接的情况
  useEffect(() => {
    if (!initialCheckDone) return;

    if (!isConnected) {
      if (location.pathname === "/login") {
        return;
      }
      const returnUrl = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?returnUrl=${returnUrl}`);
    }
  }, [isConnected, initialCheckDone]);

  useEffect(() => {
    fetchCurrencyInfo();
  }, []);

  if (!initialCheckDone) {
    return <LogoHome></LogoHome>;
  }

  return (
    <UserInfoContext.Provider
      value={{
        totalUsdValue,
        tokenInfo,
        balanceLoading,
        ethPrice,
        currencyInfo,
        showUsd,
        handleSwitchCurrency,
        handleSetCurrency,
        handleShowBalance,
        showBalance,
      }}
    >
      {children}
    </UserInfoContext.Provider>
  );
};

export const useUserInfo = () => {
  return useContext(UserInfoContext);
};
